import _ from 'lodash';
import globalStore from '$$globalStore';
import ganttViewModel from '$$ganttViewModel';
import constants from '$$helpers/constants';
import GanttWorker from '../workers/GanttWorker';
import ResourceWorker from '../workers/ResourceWorker';
import ProgressWorker from '../workers/ProgressWorker';
import prepareTaskResources from './prepareTaskResources';

const FIELDS_FOR_GANTT_WORKER_CALCULATION = ['start_date', 'end_date', 'duration', 'parent'];
const FIELDS_FOR_RESOURCE_WORKER_CALCULATION = ['type', 'duration', 'estimation'];
const FIELDS_FOR_PROGRESS_WORKER_CALCULATION = ['status', 'progress', 'parent'];

const mergeTaskData = (oldTaskFromStore, taskChanges, multiviewId = null) => {
  const mergedOldNewTaskData = {};
  const storeTaskKeys = Object.keys(oldTaskFromStore);

  storeTaskKeys.forEach(key => {
    mergedOldNewTaskData[key] = Object.prototype.hasOwnProperty.call(taskChanges, key) ? taskChanges[key] : oldTaskFromStore[key];

    if (multiviewId && key === 'parent' && mergedOldNewTaskData.parent === 1) {
      mergedOldNewTaskData.parent = 0;
    }
  });

  return mergedOldNewTaskData;
};

export default async function (tasks, multiviewId = null, fromGantt, sourceEvent) {
  const tasksMap = globalStore.getters['tasksModel/tasksByIds'];

  const updatedMap = {};
  const updatedTasksForCommit = [];
  const resourcesToTasksForCommit = [];
  const tasksCustomValuesForCommit = [];
  const userCustomValuesForCommit = [];
  const multiviewValuesForCommit = [];
  let refreshOverload = false;

  // Tasks update
  tasks.forEach(task => {
    if (!tasksMap[task.id]) {
      throw 'Not found task for updating';
    }

    const oldTaskData = multiviewId ? ganttViewModel.getTaskValuesMultiview(tasksMap[task.id]) : tasksMap[task.id];
    const taskData = mergeTaskData(oldTaskData, task, multiviewId);
    const diff = globalStore.getters['tasksModel/getNewDiff'](oldTaskData, taskData);

    if ((_.has(diff, 'status') || _.has(diff, 'priority'))) {
      tasksCustomValuesForCommit.push({ taskId: +taskData.id, ganttId: +taskData.gantt_id, tasksCustomValues: _.pick(diff, ['status', 'priority']) });
    }

    if (_.has(diff, 'open') && multiviewId) {
      delete taskData.open;
      multiviewValuesForCommit.push({ id: task.id, open: diff.open });
    }

    if (task.changedResources?.length) {
      diff.changedResources = task.changedResources;
      taskData.resources = prepareTaskResources(task.id, taskData.gantt_id, task.changedResources);

      resourcesToTasksForCommit.push({ taskId: +task.id, ganttId: +taskData.gantt_id, resourcesToTask: taskData.resources });

      refreshOverload = true;
    }

    if (task.customFields?.length) {
      diff.customFields = task.customFields;

      userCustomValuesForCommit.push({ taskId: +task.id, ganttId: +taskData.gantt_id, customFields: task.customFields });
    }

    if (!_.isEmpty(diff)) {
      updatedMap[task.id] = diff;
      updatedTasksForCommit.push(taskData);
    }

    if (taskData.resources?.length && !refreshOverload) {
      refreshOverload = _.has(diff, 'start_date') || _.has(diff, 'duration');
    }
  });

  // Run workers (if needed)
  const needGanttWorker = !fromGantt && Object.values(updatedMap).some(diff => !_.isEmpty(_.pick(diff, FIELDS_FOR_GANTT_WORKER_CALCULATION)));
  const needResourceWorker = Object.values(updatedMap).some(diff => !_.isEmpty(_.pick(diff, FIELDS_FOR_RESOURCE_WORKER_CALCULATION)));
  const needProgressWorker = Object.values(updatedMap).some(diff => !_.isEmpty(_.pick(diff, FIELDS_FOR_PROGRESS_WORKER_CALCULATION)));
  let ganttWorkerData = null;
  let resourceWorkerData = null;
  let progressWorkerData = null;

  if (needGanttWorker) {
    if (sourceEvent.type !== constants.GANTT_SOURCE_EVENT_TYPES.PROJECT_CALENDAR_UPDATE) { // spec for project calendar
      ganttWorkerData = await GanttWorker.calculate(updatedTasksForCommit, 'changeTasks', 'updateTasks');
      fromGantt = false;
    }
  }

  if (needProgressWorker) {
    if (sourceEvent.type !== constants.GANTT_SOURCE_EVENT_TYPES.SETTINGS_PROGRESS_TYPE_UPDATE) { // spec for progress type
      progressWorkerData = await ProgressWorker.calculate(updatedTasksForCommit, 'changeTasks', 'updateTasks');
      fromGantt = false;
    }
  }

  if (needResourceWorker) {
    resourceWorkerData = await ResourceWorker.calculate(updatedTasksForCommit, 'changeTasks', 'updateTasks');
    fromGantt = false;
  }

  if (!needResourceWorker && Object.values(updatedMap).some(diff => diff.changedResources?.length)) {
    if (sourceEvent.type !== constants.GANTT_SOURCE_EVENT_TYPES.WORKLOAD_CUSTOM_ESTIMATION_UPDATE) { // spec for custom estimation
      resourceWorkerData = await ResourceWorker.calculate(updatedTasksForCommit, 'changeTasks', 'updateTasksResources');
      fromGantt = false;
    }
  }

  updatedTasksForCommit.forEach(task => {
    if (ganttWorkerData && ganttWorkerData.diff.tasks[task.id]) {
      _.merge(task, ganttWorkerData.diff.tasks[task.id]);
      _.merge(updatedMap[task.id], ganttWorkerData.diff.tasks[task.id]);
    }

    if (progressWorkerData && progressWorkerData.diff.tasks[task.id]) {
      _.merge(task, progressWorkerData.diff.tasks[task.id]);
      _.merge(updatedMap[task.id], progressWorkerData.diff.tasks[task.id]);
    }

    if (resourceWorkerData && resourceWorkerData.diff.tasks[task.id]) {
      _.merge(task, resourceWorkerData.diff.tasks[task.id]);
      _.merge(updatedMap[task.id], resourceWorkerData.diff.tasks[task.id]);
    }

    if (resourceWorkerData && resourceWorkerData.diff.tasksResources[task.id]) {
      task.resources = prepareTaskResources(task.id, task.gantt_id, resourceWorkerData.diff.tasksResources[task.id]);
      updatedMap[task.id].changedResources = resourceWorkerData.diff.tasksResources[task.id];

      resourcesToTasksForCommit.push({ taskId: task.id, ganttId: task.gantt_id, resourcesToTask: task.resources });
    }
  });

  if (ganttWorkerData || progressWorkerData || resourceWorkerData) {
    const combinedData = _.merge(ganttWorkerData?.diff?.tasks, progressWorkerData?.diff?.tasks, resourceWorkerData?.diff?.tasks);

    _.forOwn(combinedData, (diff, taskId) => {
      if (!updatedMap[taskId]) {
        updatedMap[taskId] = diff;
        updatedTasksForCommit.push({ id: +taskId, ...tasksMap[taskId], ...diff });
      }

      if (tasksMap[taskId] && tasksMap[taskId].resources?.length && !refreshOverload) {
        refreshOverload = _.has(diff, 'start_date') || _.has(diff, 'duration');
      }
    });
  }

  return {
    updatedMap, updatedTasksForCommit, resourcesToTasksForCommit, tasksCustomValuesForCommit, userCustomValuesForCommit, multiviewValuesForCommit, refreshOverload, fromGantt,
  };
}
